import type { AxiosPromise } from 'axios';

import type { AdvancedSecurityForm } from '@app/features/team';
import request from '@app/utils/request';

import type { ActivityProject } from '../types';

export const updateProjectTagsApi = (projectId: string, tags: string[]) => {
    const formData = new FormData();

    tags.forEach((tag) => {
        formData.append('value[]', tag);
    });

    formData.append('pk', projectId);

    return request({
        method: 'POST',
        url: '/projects/tags',
        data: formData,
    });
};

export const getProjectsTagsApi = () =>
    request({
        method: 'GET',
        url: '/projects/tags',
    });

export const addProjectLanguagesApi = (projectId: string, languages: string[], branchName?: string): AxiosPromise => {
    const formData = new FormData();

    languages.forEach((language) => {
        formData.append('languages[]', language);
    });

    if (branchName) {
        formData.append('branch', branchName);
    }

    return request({
        method: 'POST',
        url: `/project/${projectId}/add-languages`,
        data: formData,
    });
};

export const getProjectsListApi = (tags: string[], page = 1): AxiosPromise =>
    request({
        method: 'GET',
        url: '/projects/list',
        params: {
            tags: tags.join(','),
            react: true,
            page,
        },
    });

export const setProjectActiveBranchApi = (projectId: string, branch: string): AxiosPromise =>
    request({
        method: 'GET',
        url: `/project/${projectId}/set-active-branch`,
        params: {
            branch,
        },
    });

export const setProjectsViewSizeApi = (projectIds: string[], collapse: boolean): AxiosPromise => {
    const formData = new FormData();

    projectIds.forEach((projectId) => {
        formData.append('project_ids[]', projectId);
    });
    formData.append('toggle', collapse ? 'collapse' : 'expand');

    return request({
        method: 'POST',
        url: '/projects/project-view-size',
        data: formData,
    });
};

export const postSortProjectsApi = (projectIds: string[]): AxiosPromise => {
    const formData = new FormData();

    projectIds.forEach((projectId) => {
        formData.append('list[]', projectId);
    });

    return request({
        method: 'POST',
        url: '/projects/sort-projects',
        data: formData,
    });
};

export const getProjectsMainSearchApi = ({
    query,
    objectType,
    exclude,
}: {
    query: string;
    objectType: string;
    exclude: boolean;
}): AxiosPromise =>
    request({
        method: 'GET',
        url: '/projects/main-search',
        params: {
            query,
            exclude: exclude ? 1 : 0,
            // Can't change API keys
            object_type: objectType,
        },
    });

export const postFilenamesRemove = (filenameId: string): AxiosPromise =>
    request({
        method: 'POST',
        url: `/filenames/${filenameId}/remove`,
    });

export const postFilenamesRename = (filenameId: string, newFilename: string, force = false): AxiosPromise => {
    const formData = new FormData();

    formData.append('force', force ? '1' : '0');
    formData.append('filename', newFilename);

    return request({
        method: 'POST',
        url: `/filenames/${filenameId}/rename`,
        data: formData,
    });
};

export const getActiveCount = (projects: ActivityProject[]): AxiosPromise => {
    const formData = new FormData();

    projects.forEach((project, index) => {
        formData.append(`project_info_array[${index}][projectId]`, project.id);
        formData.append(`project_info_array[${index}][branchId]`, project.branch ?? '');
    });

    return request({
        method: 'POST',
        url: '/projects/processes/active-count',
        data: formData,
    });
};

// Team settings
export const saveSecuritySettings = (data: AdvancedSecurityForm): AxiosPromise =>
    request({
        method: 'POST',
        url: '/team/advanced-security',
        data,
    });

export const getServiceProviderPublicCertificate = (data: AdvancedSecurityForm): AxiosPromise =>
    request({
        method: 'POST',
        url: '/team/advanced-security/sso/generate-certificate',
        data,
    });

export const getGlossaryTermApi = (projectId: string, termId: string): AxiosPromise =>
    request({
        method: 'GET',
        url: `/glossary/${projectId}/term/${termId}`,
    });
